import moment from 'moment';

import { feedOrderingActivityTypes } from '../../../../../context/FeedContext/config/feedTypes';
import { feedOrderTypes } from '../../../../../../utils/feed';

const PRIORITY_BUCKET_ELAPSED_TIME_IN_HOURS = 24;

/**
 * Buckets AC feed items by their claim/interaction state and returns them in display order.
 *
 * Items are always decorated with the fields the feed renders from — `orderLabelType`
 * (read by ActivityStatus) and the claimed* details (read by ClaimActivity) — regardless of
 * which ordering is applied.
 *
 * The ordering depends on whether a pod is selected:
 * - Pod selected: the feed is already scoped to that pod's clients, which effectively assigns
 *   the claims for them, so the cross-bucket priority order is redundant and would override the
 *   AC's chosen sort direction. Items keep their incoming order, which already reflects the
 *   configured direction (the reverse applied in FeedContextProvider). See S2-8290.
 * - No pod selected: items are concatenated in priority-bucket order, unchanged.
 *
 * @param {Array<object>} feedItems - Feed items in the AC's configured sort order.
 * @param {object} options
 * @param {string} options.authUserId - The logged-in AC's user id.
 * @param {(clientId: string) => {
 *   isChannelSelfClaimed: boolean,
 *   isChannelClaimed: boolean,
 *   claimedChannel: (object|undefined),
 * }} options.getChannelClaim - Resolves a client's chat-channel claim state.
 * @param {boolean} [options.isPodSelected] - Whether a pod other than "All Clients" is selected.
 * @returns {Array<object>} The decorated feed items in display order.
 */
const sortFeedItems = (feedItems = [], {
  authUserId,
  getChannelClaim,
  isPodSelected = false,
} = {}) => {
  const buckets = {
    selfClaimed: [],
    priority: [],
    selfAssigned: [],
    unassigned: [],
    assigned: [],
    claimed: [],
  };

  // The decorated items in their incoming order, i.e. the AC's configured sort direction.
  const orderedItems = [];

  // Bucket the feed items
  feedItems.forEach((feedItem) => {
    const {
      lastInteractionBy,
      type,
      createdAt,
      user,
    } = feedItem;

    const isSortableActivityType = feedOrderingActivityTypes.includes(type);
    const hasElapsedPriorityTime = moment().diff(moment(createdAt?.toDate()), 'hours')
      >= PRIORITY_BUCKET_ELAPSED_TIME_IN_HOURS;

    /*
     * Check-in activities and a client's chat carries the same claimee information.
     * If a check-in is claimed, the corresponding chat is considered claimed as well—and vice versa.
     * We keep this information in the chat channel.
     * Therefore, to determine if a check-in is claimed,
     * we need to retrieve the claimee details from the channel of the user.
     */
    const { isChannelSelfClaimed, isChannelClaimed, claimedChannel } = getChannelClaim(user);

    const isSelfClaimed = isChannelSelfClaimed;
    const isSelfAssigned = !isChannelClaimed && !!lastInteractionBy && lastInteractionBy === authUserId;
    const notInteracted = !isChannelClaimed && !lastInteractionBy;
    const isAssigned = !isChannelClaimed && !!lastInteractionBy && lastInteractionBy !== authUserId;
    const isClaimed = isChannelClaimed;

    const claimedDetails = {
      claimedAt: claimedChannel?.claimedAt,
      claimedBy: claimedChannel?.claimedBy,
      claimedFrom: claimedChannel?.claimedFrom,
      claimedByUserName: claimedChannel?.claimedByUsername,
    };

    let bucket;
    let decoratedItem;

    if (isSelfClaimed) {
      // Item claimed by the logged in user
      bucket = buckets.selfClaimed;
      decoratedItem = { ...feedItem, ...claimedDetails, orderLabelType: feedOrderTypes.SELF_CLAIMED };
    } else if (isSortableActivityType && hasElapsedPriorityTime) {
      // Item eligible for sorting and has been created more than 24 hours ago
      let orderLabelType = null;
      if (isSelfAssigned) {
        orderLabelType = feedOrderTypes.SELF_ASSIGNED;
      } else if (isAssigned) {
        orderLabelType = feedOrderTypes.ASSIGNED;
      } else if (isClaimed) {
        orderLabelType = feedOrderTypes.CLAIMED;
      }
      bucket = buckets.priority;
      decoratedItem = { ...feedItem, ...claimedDetails, orderLabelType };
    } else if (isSelfAssigned) {
      // Item unclaimed, but attached to the logged in user due to last interaction
      bucket = buckets.selfAssigned;
      decoratedItem = { ...feedItem, orderLabelType: feedOrderTypes.SELF_ASSIGNED };
    } else if (notInteracted) {
      /* Item unclaimed and unassigned to any user. This will also include items that aren't intended
       * to be sorted as they won't have these attributes either
       */
      bucket = buckets.unassigned;
      decoratedItem = feedItem;
    } else if (isAssigned) {
      // Item unclaimed, but assigned to a different user
      bucket = buckets.assigned;
      decoratedItem = { ...feedItem, orderLabelType: feedOrderTypes.ASSIGNED };
    } else if (isClaimed) {
      // Item claimed by a different user
      bucket = buckets.claimed;
      decoratedItem = { ...feedItem, ...claimedDetails, orderLabelType: feedOrderTypes.CLAIMED };
    } else {
      return;
    }

    bucket.push(decoratedItem);
    orderedItems.push(decoratedItem);
  });

  /*
   * With a pod selected the AC's configured sort direction wins outright, so return the items
   * in the order they came in rather than regrouping them by claim/interaction bucket.
   */
  if (isPodSelected) {
    return orderedItems;
  }

  // Concatenate buckets in the specified order
  return [
    ...buckets.selfClaimed,
    ...buckets.priority,
    ...buckets.selfAssigned,
    ...buckets.unassigned,
    ...buckets.assigned,
    ...buckets.claimed,
  ];
};

export { sortFeedItems, PRIORITY_BUCKET_ELAPSED_TIME_IN_HOURS };
export default sortFeedItems;
